import React from 'react';
import type { FormikHelpers } from 'formik';
import { FieldArray, Formik } from 'formik';
import { isEmpty, noop } from 'lodash';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import Divider from '~/components/ui/atoms/Divider';
import type { PhoneProfileInfo, PhoneProfilePatchRequest } from '~/server_shared/openapi/data-contracts';
import { yupPhoneValidation } from '~/Utils/phoneValidationUtils';

import CardDialog from '../../../../../CardDialog';
import CheckboxFormik from '../../../../../CheckboxFormik';
import { ArrowIntegerIcon, CalendarEmptyIcon, PlusIcon, TrashIcon } from '../../../../../icons';
import TextFieldFormik, { DatePickerTextFieldFormik, TimePickerTextFieldFormik } from '../../../../../TextFieldFormik';

import { useStyles } from '../../../../../../assets/styles';
import colors from '../../../../../../assets/colors.module.scss';
import styles from './WorkingHoursConfig.module.scss';

const useCustomStyles = makeStyles()(() => ({
  textField: {
    maxWidth: 150,
    '& .MuiIconButton-root': {
      paddingRight: 0,
    },
  },
  datePicker: {
    width: '100%',
    '& .MuiIconButton-root': {
      paddingRight: 5,
    },
  },
}));

interface BusinessHoursDay {
  label: string;
  is_work_day: boolean;
  working_hours_start: string | null;
  working_hours_end: string | null;
}

interface AdditionalNonWorkingDay {
  start_date: string | Date | null;
  end_date: string | Date | null;
}

type VoicemailOrForwardChoice = 'should_send_to_voicemail' | 'should_forward_call' | null;

interface FormValues {
  send_to_voicemail_or_forward_call: VoicemailOrForwardChoice;
  forward_call_phone_number: string;
  business_hours: BusinessHoursDay[];
  additional_non_working_days: AdditionalNonWorkingDay[];
}

interface BusinessHoursConfiguration {
  should_send_to_voicemail?: boolean;
  should_forward_call?: boolean;
  forward_call_phone_number?: string;
  business_hours?: BusinessHoursDay[];
  additional_non_working_days?: AdditionalNonWorkingDay[];
}

const businessHoursDefaultConfig: BusinessHoursDay[] = [
  {
    label: 'Monday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
  {
    label: 'Tuesday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
  {
    label: 'Wednesday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
  {
    label: 'Thursday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
  {
    label: 'Friday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
  {
    label: 'Saturday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
  {
    label: 'Sunday',
    is_work_day: true,
    working_hours_start: null,
    working_hours_end: null,
  },
];

const getVoicemailOrForwardCallValue = (configuration: BusinessHoursConfiguration = {}): VoicemailOrForwardChoice => {
  if (configuration.should_forward_call) {
    return 'should_forward_call';
  }
  if (configuration.should_send_to_voicemail) {
    return 'should_send_to_voicemail';
  }
  return null;
};

type WorkingHoursSubmitValues = Pick<PhoneProfilePatchRequest, 'business_hours_configuration_enabled'> & {
  business_hours_configuration: BusinessHoursConfiguration;
};

interface EditWorkingHoursDialogProps {
  configuration?: BusinessHoursConfiguration;
  onCancel?: () => void;
  onSubmit: (values: WorkingHoursSubmitValues) => Promise<void>;
}

const EditWorkingHoursDialog: React.FC<EditWorkingHoursDialogProps> = ({
  configuration = {},
  onCancel = noop,
  onSubmit,
}) => {
  const { classes } = useStyles();
  const { classes: customClasses } = useCustomStyles();

  const handleSubmit = async (values: FormValues, formikProps: FormikHelpers<FormValues>) => {
    try {
      await onSubmit({
        business_hours_configuration_enabled: true,
        business_hours_configuration: {
          should_send_to_voicemail: values.send_to_voicemail_or_forward_call === 'should_send_to_voicemail',
          should_forward_call: values.send_to_voicemail_or_forward_call === 'should_forward_call',
          forward_call_phone_number: values.forward_call_phone_number,
          business_hours: values.business_hours,
          additional_non_working_days: values.additional_non_working_days,
        },
      });
      onCancel();
    } catch {
      formikProps.setSubmitting(false);
    }
  };

  const yupTimeValidation = Yup.string().matches(
    /^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/,
    'Invalid time supplied'
  );

  const validationSchema = Yup.object().shape({
    send_to_voicemail_or_forward_call: Yup.string()
      .oneOf(['should_send_to_voicemail', 'should_forward_call'], 'Required')
      .required('Required')
      .nullable(),
    forward_call_phone_number: yupPhoneValidation.when('send_to_voicemail_or_forward_call', {
      is: 'should_forward_call',
      then: yupPhoneValidation.required('Required'),
    }),
    business_hours: Yup.array().of(
      Yup.object().shape({
        label: Yup.string(),
        is_work_day: Yup.boolean(),
        working_hours_start: yupTimeValidation.nullable().when('is_work_day', {
          is: true,
          then: yupTimeValidation.required('Required').nullable(),
        }),
        working_hours_end: yupTimeValidation
          .nullable()
          .when(['is_work_day'], {
            is: true,
            then: yupTimeValidation.nullable().required('Required').nullable(),
          })
          .when(['working_hours_start'], {
            is: (val: unknown) => !!val,
            then: yupTimeValidation
              .test('is-greater-than-start', 'End time should be after start time', function (value) {
                const { working_hours_start } = this.parent as BusinessHoursDay;
                return !!value && !!working_hours_start && value > working_hours_start;
              })
              .nullable(),
          }),
      })
    ),
    additional_non_working_days: Yup.array().of(
      Yup.object().shape({
        start_date: Yup.date().required('Required').nullable(),
        end_date: Yup.date()
          .min(Yup.ref('start_date'), 'End date must be after or equal to start date')
          .required('Required')
          .nullable(),
      })
    ),
  });

  const initialValues: FormValues = {
    send_to_voicemail_or_forward_call: getVoicemailOrForwardCallValue(configuration),
    forward_call_phone_number: configuration.forward_call_phone_number ?? '',
    business_hours: configuration.business_hours ?? businessHoursDefaultConfig,
    additional_non_working_days: configuration.additional_non_working_days ?? [{ start_date: null, end_date: null }],
  };

  return (
    <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      {({ isSubmitting, handleSubmit: formikHandleSubmit, values }) => (
        <CardDialog
          title="Business hours & holidays"
          isDialog
          maxWidth="sm"
          fullWidth
          onClose={onCancel}
          preventClose={isSubmitting}
        >
          <div className={styles.workingHoursRadioGroupWrapper}>
            <div>
              <Typography variant="subtitle1">Outside business hours</Typography>
              <Typography variant="body1">Choose how to handle calls received outside of business hours</Typography>
            </div>
            <RadioButtonGroupFormik
              id="send_to_voicemail_or_forward_call"
              direction="row"
              options={[
                { text: 'Send to voicemail', optionValue: 'should_send_to_voicemail' },
                { text: 'Forward to phone number', optionValue: 'should_forward_call' },
              ]}
              variant="subtitle1"
            />
            {values.send_to_voicemail_or_forward_call === 'should_forward_call' ? (
              <TextFieldFormik id="forward_call_phone_number" label="Add Phone Number" />
            ) : null}
          </div>
          <div className={styles.workingHoursBusinessDaysWrapper}>
            <FieldArray name="business_hours">
              {() => (
                <>
                  {values.business_hours.map((day, index) => (
                    <div key={index} className={styles.businessDayRow}>
                      <CheckboxFormik
                        containerClassName={styles.dayCheckbox}
                        id={`business_hours.${index}.is_work_day`}
                        label={day.label}
                      />
                      <div className={styles.timePickersContainer}>
                        <TimePickerTextFieldFormik
                          setTouchedOnChange
                          className={customClasses.textField}
                          id={`business_hours.${index}.working_hours_start`}
                          keyboardIcon={<ArrowIntegerIcon size={16} iconColor={colors.inputIcon} />}
                        />
                        <span className={styles.timeTextSeparator}>to</span>
                        <TimePickerTextFieldFormik
                          setTouchedOnChange
                          className={customClasses.textField}
                          id={`business_hours.${index}.working_hours_end`}
                          keyboardIcon={<ArrowIntegerIcon size={16} iconColor={colors.inputIcon} />}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </FieldArray>
          </div>
          <div className={styles.holidaysWrapper}>
            <Typography variant="subtitle1">Set Holidays</Typography>
            <div className={styles.dateListContainer}>
              <FieldArray name="additional_non_working_days">
                {(arrayHelpers) => (
                  <div className={styles.datePickerContainer}>
                    {values.additional_non_working_days.map((_day, index) => (
                      <div key={index} className={styles.additionalNonWorkingDayRow}>
                        <DatePickerTextFieldFormik
                          containerClassName={styles.datePickerTextFieldFormikContainer}
                          id={`additional_non_working_days.${index}.start_date`}
                          className={customClasses.datePicker}
                          label="Start Date"
                          keyboardIcon={<CalendarEmptyIcon size={16} iconColor={colors.textPrimary} />}
                        />
                        <span className={styles.dateTextSeparator}>to</span>
                        <DatePickerTextFieldFormik
                          containerClassName={styles.datePickerTextFieldFormikContainer}
                          id={`additional_non_working_days.${index}.end_date`}
                          className={customClasses.datePicker}
                          label="End Date"
                          keyboardIcon={<CalendarEmptyIcon size={16} iconColor={colors.textPrimary} />}
                        />
                        <IconButton className={styles.trashIconButton} onClick={() => arrayHelpers.remove(index)}>
                          <TrashIcon size={20} iconColor={colors.textPrimary} />
                        </IconButton>
                      </div>
                    ))}
                    <Button
                      className={styles.addDateButton}
                      variant="text"
                      color="primary"
                      startIcon={<PlusIcon iconColor={colors.buttonLink} size={14} />}
                      onClick={() => arrayHelpers.push({ start_date: null, end_date: null })}
                    >
                      ADD DATE
                    </Button>
                  </div>
                )}
              </FieldArray>
            </div>
          </div>
          <div className={classes.buttonsContainer}>
            <CancelButton disabled={isSubmitting} onClick={onCancel} />
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                formikHandleSubmit();
              }}
            >
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

interface WorkingHoursConfigViewProps {
  profile: PhoneProfileInfo;
}

const WorkingHoursConfigView: React.FC<WorkingHoursConfigViewProps> = ({ profile }) => {
  const workingHoursConfiguration = profile.business_hours_configuration as BusinessHoursConfiguration | null;
  const header = (
    <>
      <Typography variant="subtitle1">Business hours & holidays</Typography>
      <Typography variant="caption">
        Specify the scheduled business hours when users are available to take direct calls. By default, the lines are
        open 24/7.
      </Typography>
    </>
  );

  if (
    !profile.business_hours_configuration_enabled ||
    !workingHoursConfiguration ||
    isEmpty(workingHoursConfiguration)
  ) {
    return header;
  }

  return (
    <>
      {header}
      <div className={styles.detailsContainer}>
        <div className={styles.detailsItem}>
          <Typography variant="subtitle2" className={styles.detailsItemTitle}>
            Business Hours:
          </Typography>
          <div>
            {workingHoursConfiguration.business_hours?.map((day, index) => (
              <div key={index} className={styles.dayDetailsContainer}>
                <Typography variant="subtitle2" className={styles.itemDetailsLabel}>
                  {day.label}
                </Typography>
                <div className={styles.hoursContainer}>
                  {day.is_work_day ? (
                    <>
                      <span className={styles.timeDigits}>{day.working_hours_start}</span>
                      <span>to</span>
                      <span className={styles.timeDigits}>{day.working_hours_end}</span>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
        <Divider orientation="vertical" flexItem className={styles.detailsDivider} />
        <div className={styles.detailsItem}>
          <Typography variant="subtitle2" className={styles.detailsItemTitle}>
            Outside Business Hours:
          </Typography>
          <div className={styles.outOfOfficeItemContainer}>
            <Typography variant="subtitle2" className={styles.itemDetailsLabel}>
              {workingHoursConfiguration.should_forward_call
                ? 'Send to Phone:'
                : workingHoursConfiguration.should_send_to_voicemail
                  ? 'Send to voicemail'
                  : null}
            </Typography>
            {workingHoursConfiguration.should_forward_call && workingHoursConfiguration.forward_call_phone_number ? (
              <span className={styles.forwardCallPhoneNumber}>
                {workingHoursConfiguration.forward_call_phone_number}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export { EditWorkingHoursDialog, WorkingHoursConfigView };
